import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterControls = ({ onFiltersChange, onReset }) => {
  const [filters, setFilters] = useState({
    minAmount: '',
    maxHops: '10',
    timeRange: '24h',
    addressTypes: [],
    riskLevels: [],
    confidenceThreshold: '0.5'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const timeRangeOptions = [
    { value: '1h', label: 'Last Hour' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const addressTypeOptions = [
    { value: 'source', label: 'Source Addresses' },
    { value: 'intermediate', label: 'Intermediate Wallets' },
    { value: 'mixer', label: 'Mixing Services' },
    { value: 'exchange', label: 'Exchange Wallets' },
    { value: 'destination', label: 'Destination Addresses' }
  ];

  const riskLevelOptions = [
    { value: 'high', label: 'High Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'low', label: 'Low Risk' },
    { value: 'unknown', label: 'Unknown Risk' }
  ];

  const confidenceOptions = [
    { value: '0.9', label: 'Very High (90%+)' },
    { value: '0.7', label: 'High (70%+)' },
    { value: '0.5', label: 'Medium (50%+)' },
    { value: '0.3', label: 'Low (30%+)' },
    { value: '0.0', label: 'All Transactions' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      minAmount: '',
      maxHops: '10',
      timeRange: '24h',
      addressTypes: [],
      riskLevels: [],
      confidenceThreshold: '0.5'
    };
    setFilters(resetFilters);
    onReset?.(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.minAmount) count++;
    if (filters?.maxHops !== '10') count++;
    if (filters?.timeRange !== '24h') count++;
    if (filters?.addressTypes?.length > 0) count++;
    if (filters?.riskLevels?.length > 0) count++;
    if (filters?.confidenceThreshold !== '0.5') count++;
    return count;
  };

  return (
    <div className="bg-surface rounded-lg border border-border shadow-forensic-card">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Analysis Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={getActiveFilterCount() === 0}
          >
            <Icon name="RotateCcw" size={16} className="mr-2" />
            Reset
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
            />
          </Button>
        </div>
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Minimum Amount (BTC)"
            type="number"
            placeholder="0.001"
            value={filters?.minAmount}
            onChange={(e) => handleFilterChange('minAmount', e?.target?.value)}
            description="Filter transactions below this amount"
          />
          
          <Select
            label="Maximum Hops"
            options={[
              { value: '3', label: '3 Hops' },
              { value: '5', label: '5 Hops' },
              { value: '10', label: '10 Hops' },
              { value: '20', label: '20 Hops' },
              { value: 'unlimited', label: 'Unlimited' }
            ]}
            value={filters?.maxHops}
            onChange={(value) => handleFilterChange('maxHops', value)}
            description="Limit transaction depth analysis"
          />
          
          <Select
            label="Time Range"
            options={timeRangeOptions}
            value={filters?.timeRange}
            onChange={(value) => handleFilterChange('timeRange', value)}
            description="Filter by transaction timestamp"
          />
        </div>
      </div>
      {/* Advanced Filters - Expandable */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="pt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Address Types"
                options={addressTypeOptions}
                value={filters?.addressTypes}
                onChange={(value) => handleFilterChange('addressTypes', value)}
                multiple
                searchable
                description="Include specific address types"
                placeholder="Select address types..."
              />
              
              <Select
                label="Risk Levels"
                options={riskLevelOptions}
                value={filters?.riskLevels}
                onChange={(value) => handleFilterChange('riskLevels', value)}
                multiple
                searchable
                description="Filter by risk assessment"
                placeholder="Select risk levels..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Confidence Threshold"
                options={confidenceOptions}
                value={filters?.confidenceThreshold}
                onChange={(value) => handleFilterChange('confidenceThreshold', value)}
                description="Minimum confidence for transaction links"
              />
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">
                  Pattern Detection
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFilterChange('detectPeelChains', !filters?.detectPeelChains)}
                    className={filters?.detectPeelChains ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Icon name="GitBranch" size={14} className="mr-1" />
                    Peel Chains
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFilterChange('detectMixing', !filters?.detectMixing)}
                    className={filters?.detectMixing ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Icon name="Shuffle" size={14} className="mr-1" />
                    Mixing
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFilterChange('detectExchanges', !filters?.detectExchanges)}
                    className={filters?.detectExchanges ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Icon name="Building2" size={14} className="mr-1" />
                    Exchanges
                  </Button>
                </div>
                <p className="text-xs text-text-secondary">
                  Highlight specific laundering patterns
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Filter Summary */}
      {getActiveFilterCount() > 0 && (
        <div className="px-4 py-3 bg-muted border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="Info" size={16} />
              <span>
                Showing transactions with {getActiveFilterCount()} filter{getActiveFilterCount() > 1 ? 's' : ''} applied
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-primary"
            >
              {isExpanded ? 'Show Less' : 'Show More'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;